import { provideHttpClient, withInterceptors } from "@angular/common/http";
import {
  ApplicationConfig,
  EnvironmentInjector,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideZonelessChangeDetection,
  runInInjectionContext,
} from "@angular/core";
import { provideNativeDateAdapter } from "@angular/material/core";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { provideRouter } from "@angular/router";
import { ApplicationinsightsAngularpluginErrorService } from "@microsoft/applicationinsights-angularplugin-js";
import { provideEffects } from "@ngrx/effects";
import { provideStore } from "@ngrx/store";
import { provideStoreDevtools } from "@ngrx/store-devtools";
import { provideOAuthClient } from "angular-oauth2-oidc";

// import { provideMarkdown } from "ngx-markdown";
import { environment } from "../environments/environment";
import { appGuards } from "./core/_guards";
import { authInterceptor } from "./core/_interceptors/auth.interceptor";
import {
  appServices,
  AuthenticationService,
  AuthModeService,
  ExternalAuthenticationService,
  PortalAuthenticationService,
} from "./core/_services";
import { apiServices, appEffects, appReducers } from "./core/store/store";
import { authAppInitializerFactory } from "./core/utils/auth-app-initializer.factory";
import { probeAuthMode } from "./core/utils/auth-mode-initializer.factory";
import { provideSaffronRegistry } from "./core/utils/saffron.registry.provider";
import { routes } from "./routes";
import { FormatDatePipe } from "./shared/_pipes/format-date.pipe";
import { FormatRelativeDatePipe } from "./shared/_pipes/format-relative-date.pipe";

export const appConfig: ApplicationConfig = {
  providers: [
    appServices,
    appGuards,
    apiServices,
    provideSaffronRegistry(),
    provideZonelessChangeDetection(),
    provideRouter(routes),
    provideAnimationsAsync(),
    provideStore(appReducers),
    provideEffects(appEffects),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideOAuthClient(),
    // Ordering is load-bearing: probeAuthMode() must complete before the
    // AuthenticationService factory runs (during auth init), otherwise the
    // factory sees the default mode and binds the wrong implementation.
    // APP_INITIALIZER multi-providers run in parallel via Promise.all, so the
    // two steps must share a single initializer to be sequenced.
    //
    // The captured injector is required because Angular's injection context
    // is torn down across `await` boundaries — without runInInjectionContext,
    // the inject() inside authAppInitializerFactory throws after probeAuthMode
    // resolves.
    provideAppInitializer(async () => {
      const injector = inject(EnvironmentInjector);
      await probeAuthMode();
      await runInInjectionContext(injector, authAppInitializerFactory);
    }),
    {
      provide: AuthenticationService,
      useFactory: (): AuthenticationService =>
        inject(AuthModeService).mode === "portal"
          ? inject(PortalAuthenticationService)
          : inject(ExternalAuthenticationService),
    },
    provideNativeDateAdapter(),
    FormatDatePipe,
    FormatRelativeDatePipe,
    {
      provide: ErrorHandler,
      useClass: ApplicationinsightsAngularpluginErrorService,
    },
    provideStoreDevtools({ maxAge: 25, logOnly: environment.production }),
  ],
};
